import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';

const getLeadStatusColorClass = (status) => {
  const s = (status || 'New').toLowerCase();
  switch (s) {
    case 'new':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'hot':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'warm':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'cold':
      return 'bg-slate-50 text-slate-655 border-slate-200';
    case 'lost':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'architect/designer':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200';
  }
};

const maskPhoneNumber = (phone) => {
  if (!phone || phone === 'N/A') return 'N/A';
  const cleanPhone = phone.trim();
  if (cleanPhone.length < 4) return cleanPhone;
  return '••••••' + cleanPhone.slice(-4);
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const MembershipAuditTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Filters State
  const [membershipFilters, setMembershipFilters] = useState({ startDate: '', endDate: '' });
  const [membershipCategory, setMembershipCategory] = useState('paid'); // 'paid', 'free', 'expired'
  const [selectedPaidFilter, setSelectedPaidFilter] = useState('ALL'); // 'ALL', 'PROFESSIONAL', 'PREMIUM', 'YET_TO_EXPIRE'
  
  // Pagination State
  const [auditPage, setAuditPage] = useState(0);
  const [auditPageSize, setAuditPageSize] = useState(25);

  // Data State
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI Interactive State
  const [revealedAuditPhone, setRevealedAuditPhone] = useState(null);

  const fetchMemberships = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getAdminMemberships();
      if (res.success && res.data) {
        setMemberships(res.data.memberships || []);
      } else {
        setError(res.error || 'Failed to fetch memberships registry.');
      }
    } catch (err) {
      setError('An error occurred while fetching memberships.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const handleLeadStatusChange = async (vendorEmail, newStatus) => {
    try {
      const res = await vendorService.updateLeadStatus(vendorEmail, newStatus);
      if (res.success) {
        toast.success(`Successfully updated lead status to ${newStatus}`);
        setMemberships(prevMemberships =>
          prevMemberships.map(m =>
            m.vendor_email === vendorEmail ? { ...m, lead_status: newStatus } : m
          )
        );
      } else {
        toast.error(res.error || 'Failed to update lead status');
      }
    } catch (err) {
      toast.error('An error occurred while updating lead status');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading memberships...</span>
      </div>
    );
  }

  // Filtered by date range
  const filteredMembershipsByDate = memberships.filter(item => {
    if (!item.created_at) return true;
    const itemDateStr = item.created_at.substring(0, 10);
    if (membershipFilters.startDate && itemDateStr < membershipFilters.startDate) return false;
    if (membershipFilters.endDate && itemDateStr > membershipFilters.endDate) return false;
    return true;
  });

  // Group memberships into Paid vs Free vs Expired
  const paidMemberships = filteredMembershipsByDate.filter(m => m.plan_name !== 'FREE' && m.status !== 'EXPIRED');
  const freeMemberships = filteredMembershipsByDate.filter(m => m.plan_name === 'FREE' && m.status !== 'EXPIRED');
  const expiredMemberships = filteredMembershipsByDate.filter(m => m.status === 'EXPIRED');

  // Filter Paid memberships by selectedPaidFilter
  const filteredPaidMemberships = paidMemberships.filter(m => {
    if (selectedPaidFilter === 'ALL') return true;
    if (selectedPaidFilter === 'PROFESSIONAL') return m.plan_name === 'PROFESSIONAL';
    if (selectedPaidFilter === 'PREMIUM') return m.plan_name === 'PREMIUM';
    if (selectedPaidFilter === 'YET_TO_EXPIRE') return m.status === 'YET_TO_EXPIRE';
    return true;
  });

  let activeList = [];
  if (membershipCategory === 'paid') {
    activeList = filteredPaidMemberships;
  } else if (membershipCategory === 'free') {
    activeList = freeMemberships;
  } else if (membershipCategory === 'expired') {
    activeList = expiredMemberships;
  }

  const auditTotalPages = Math.ceil(activeList.length / auditPageSize);
  const safeAuditPage = Math.min(auditPage, Math.max(0, auditTotalPages - 1));
  const displayedAuditList = activeList.slice(safeAuditPage * auditPageSize, (safeAuditPage + 1) * auditPageSize);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Tab Title & Date Filters */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Memberships & Sales Audit</h2>
        </div>
        
        {/* Date Filter Inputs */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-100 p-3 rounded-xl select-none">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-555 uppercase tracking-wider">Date:</span>
            <DateRangePicker
              startDate={membershipFilters.startDate}
              endDate={membershipFilters.endDate}
              onChange={(start, end) => {
                setMembershipFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                setAuditPage(0);
              }}
              placeholder="Select Date Range"
            />
          </div>
          {(membershipFilters.startDate || membershipFilters.endDate) && (
            <button
              onClick={() => {
                setMembershipFilters({ startDate: '', endDate: '' });
                setAuditPage(0);
              }}
              className="px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Three Categories (Paid vs Free vs Expired) */}
      <div className="flex border-b border-gray-200 mb-6 gap-6 text-sm font-semibold select-none">
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'paid' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('paid');
            setAuditPage(0);
          }}
        >
          Paid Plans ({paidMemberships.length})
        </button>
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'free' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('free');
            setAuditPage(0);
          }}
        >
          Free Plans ({freeMemberships.length})
        </button>
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'expired' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('expired');
            setAuditPage(0);
          }}
        >
          Expired Plans ({expiredMemberships.length})
        </button>
      </div>

      {/* Filter by Plan Name/Status (only for Paid Plan category) */}
      {membershipCategory === 'paid' && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6 bg-gray-50 border border-gray-100 p-4 rounded-xl select-none">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Filter Paid Plans:</span>

          {/* Desktop view (Pills list) */}
          <div className="hidden md:flex flex-wrap gap-2">
            {[
              { id: 'ALL', label: 'All Paid Plans' },
              { id: 'PROFESSIONAL', label: 'Professional Plan' },
              { id: 'PREMIUM', label: 'Premium Plan' },
              { id: 'YET_TO_EXPIRE', label: 'Yet to Expire (1 Month Remaining)' }
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => {
                  setSelectedPaidFilter(opt.id);
                  setAuditPage(0);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${selectedPaidFilter === opt.id
                  ? 'bg-[#2ab391] text-white border-[#2ab391] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Mobile view (Dropdown select) */}
          <div className="block md:hidden w-full sm:w-auto">
            <select
              value={selectedPaidFilter}
              onChange={(e) => {
                setSelectedPaidFilter(e.target.value);
                setAuditPage(0);
              }}
              className="w-full sm:w-64 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
            >
              <option value="ALL">All Paid Plans</option>
              <option value="PROFESSIONAL">Professional Plan</option>
              <option value="PREMIUM">Premium Plan</option>
              <option value="YET_TO_EXPIRE">Yet to Expire (1 Month Remaining)</option>
            </select>
          </div>
        </div>
      )}

      {/* Record Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
              <th className="p-4 pl-6">Company / Vendor Email</th>
              <th className="p-4">Plan Name</th>
              <th className="p-4">Mobile Number</th>
              {membershipCategory !== 'free' && <th className="p-4">Dates</th>}
              {membershipCategory === 'free' && <th className="p-4">Lead Status</th>}
              <th className="p-4 pr-6">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {displayedAuditList.length === 0 ? (
              <tr>
                <td colSpan={membershipCategory !== 'free' ? 5 : 5} className="p-12 text-center text-gray-400 font-semibold">
                  No records found for the selected category.
                </td>
              </tr>
            ) : (
              displayedAuditList.map(m => (
                <tr key={m.id || m.vendor_email} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-left">
                    <div className="font-semibold text-gray-800">{m.company_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{m.vendor_email}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md ${m.plan_name === 'PREMIUM' ? 'bg-violet-100 text-violet-700' :
                      m.plan_name === 'PROFESSIONAL' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                      {m.plan_name}
                    </span>
                  </td>
                  <td className="p-4 text-sm font-semibold text-gray-700 text-left">
                    {m.phone && m.phone !== 'N/A' ? (
                      <button
                        onClick={() => setRevealedAuditPhone(prev => prev === (m.id || m.vendor_email) ? null : (m.id || m.vendor_email))}
                        className="flex items-center gap-1.5 hover:text-indigo-600 transition-colors duration-150 focus:outline-none"
                        title={revealedAuditPhone === (m.id || m.vendor_email) ? "Hide phone number" : "Click to view phone number"}
                      >
                        <span className="font-mono text-gray-600 tracking-wider">
                          {revealedAuditPhone === (m.id || m.vendor_email) ? m.phone : maskPhoneNumber(m.phone)}
                        </span>
                        {revealedAuditPhone === (m.id || m.vendor_email) ? (
                          <EyeOff size={14} className="text-gray-400 hover:text-indigo-500" />
                        ) : (
                          <Eye size={14} className="text-gray-400 hover:text-indigo-500" />
                        )}
                      </button>
                    ) : (
                      <span className="text-gray-400 font-normal">N/A</span>
                    )}
                  </td>
                  {membershipCategory !== 'free' && (
                    <td className="p-4 text-xs font-semibold text-gray-700 text-left">
                      {m.start_date ? (
                        <>
                          <div>Start: <span className="font-normal text-gray-600">{new Date(m.start_date).toLocaleDateString('en-IN')}</span></div>
                          <div className="mt-1 text-gray-700">End: <span className="font-normal text-gray-600">{new Date(m.end_date).toLocaleDateString('en-IN')}</span></div>
                        </>
                      ) : (
                        <span className="text-gray-400 font-normal">No Dates</span>
                      )}
                    </td>
                  )}
                  {membershipCategory === 'free' && (
                    <td className="p-4 text-left">
                      <select
                        value={m.lead_status || 'New'}
                        onChange={(e) => handleLeadStatusChange(m.vendor_email, e.target.value)}
                        className={`border rounded-lg px-2 py-1 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-[#2ab391] cursor-pointer transition-colors ${getLeadStatusColorClass(m.lead_status)}`}
                      >
                        <option value="New" className="bg-blue-50 text-blue-700 font-bold">New</option>
                        <option value="Hot" className="bg-emerald-50 text-emerald-700 font-bold">Hot</option>
                        <option value="Warm" className="bg-amber-50 text-amber-700 font-bold">Warm</option>
                        <option value="Cold" className="bg-slate-50 text-slate-600 font-bold">Cold</option>
                        <option value="Lost" className="bg-rose-50 text-rose-700 font-bold">Lost</option>
                        <option value="Architect/Designer" className="bg-purple-50 text-purple-700 font-bold">Architect/Designer</option>
                      </select>
                    </td>
                  )}
                  <td className="p-4 pr-6 text-left">
                    <span className={`px-2.5 py-1 rounded text-xs font-bold ${m.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' :
                      m.status === 'YET_TO_EXPIRE' ? 'bg-amber-50 text-amber-700 border border-amber-200/50' :
                        'bg-rose-50 text-rose-700'
                      }`}>
                      {m.status === 'YET_TO_EXPIRE' ? 'Yet to Expire (Sales)' : m.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {activeList.length === 0 ? (
            'No records available'
          ) : (
            `Showing ${safeAuditPage * auditPageSize + 1} to ${Math.min((safeAuditPage + 1) * auditPageSize, activeList.length)} of ${activeList.length} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={auditPageSize}
                onChange={(e) => {
                  setAuditPageSize(Number(e.target.value));
                  setAuditPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {auditTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setAuditPage(p => Math.max(0, p - 1))}
                disabled={safeAuditPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(safeAuditPage, auditTotalPages).map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                        ...
                      </span>
                    );
                  }
                  const isActive = safeAuditPage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setAuditPage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                        ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {safeAuditPage + 1} / {auditTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setAuditPage(p => Math.min(auditTotalPages - 1, p + 1))}
                disabled={safeAuditPage >= auditTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipAuditTab;
